import { apiService, Campaign } from "./api";

// Campaigns service for managing campaigns cache
export class CampaignsService {
  private static instance: CampaignsService;
  private campaigns: Campaign[] = [];
  private isLoaded: boolean = false;
  private isLoading: boolean = false;
  private loadPromise: Promise<Campaign[]> | null = null;

  private constructor() {}

  public static getInstance(): CampaignsService {
    if (!CampaignsService.instance) {
      CampaignsService.instance = new CampaignsService();
    }
    return CampaignsService.instance;
  }

  // Get campaigns with caching
  public async getCampaigns(): Promise<Campaign[]> {
    // If already loaded, return cached campaigns
    if (this.isLoaded) {
      return this.campaigns;
    }

    // If currently loading, return the existing promise
    if (this.isLoading && this.loadPromise) {
      return await this.loadPromise;
    }

    // Start loading
    this.isLoading = true;
    this.loadPromise = this.loadCampaigns();

    try {
      const campaigns = await this.loadPromise;
      return campaigns;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  // Load campaigns from API
  private async loadCampaigns(): Promise<Campaign[]> {
    try {
      const response = await apiService.getCampaigns();
      if (response.success) {
        this.campaigns = response.data;
        this.isLoaded = true;
        console.log("Campaigns loaded and cached:", this.campaigns);
        return this.campaigns;
      } else {
        throw new Error("Failed to load campaigns");
      }
    } catch (error) {
      console.error("Failed to load campaigns:", error);
      this.isLoaded = false;
      this.campaigns = [];
      throw error;
    }
  }

  // Get campaign by ID
  public getCampaignById(campaignId: string): Campaign | undefined {
    return this.campaigns.find((campaign) => campaign._id === campaignId);
  }

  // Get campaigns by status
  public getCampaignsByStatus(status: number): Campaign[] {
    return this.campaigns.filter((campaign) => campaign.status === status);
  }

  // Get active campaigns
  public getActiveCampaigns(): Campaign[] {
    return this.getCampaignsByStatus(1);
  }

  // Get inactive campaigns
  public getInactiveCampaigns(): Campaign[] {
    return this.getCampaignsByStatus(0);
  }

  // Calculate total codes across all campaigns
  public getTotalCodes(): number {
    return this.campaigns.reduce((sum, campaign) => {
      // Calculate total codes based on the formula: (total_gift * rate_code) / rate_gift
      const totalCodes = Math.floor(
        (campaign.total_gift * campaign.rate_code) / campaign.rate_gift
      );
      return sum + totalCodes;
    }, 0);
  }

  // Calculate used codes from API data
  public getUsedCodes(): number {
    return this.campaigns.reduce((sum, campaign) => {
      return sum + (campaign.num_code_used || 0);
    }, 0);
  }

  // Calculate remaining codes
  public getRemainingCodes(): number {
    return this.getTotalCodes() - this.getUsedCodes();
  }

  // Get campaigns by product
  public getCampaignsByProduct(productName: string): Campaign[] {
    return this.campaigns.filter((campaign) =>
      campaign.product_name.toLowerCase().includes(productName.toLowerCase())
    );
  }

  // Get unique products
  public getUniqueProducts(): string[] {
    const products = this.campaigns.map((campaign) => campaign.product_name);
    return Array.from(new Set(products));
  }

  // Get campaigns within date range
  public getCampaignsInDateRange(
    startDate: string,
    endDate: string
  ): Campaign[] {
    const start = new Date(startDate);
    const end = new Date(endDate);

    return this.campaigns.filter((campaign) => {
      const campaignStart = new Date(campaign.start_date);
      const campaignEnd = new Date(campaign.end_date);

      return (
        (campaignStart >= start && campaignStart <= end) ||
        (campaignEnd >= start && campaignEnd <= end) ||
        (campaignStart <= start && campaignEnd >= end)
      );
    });
  }

  // Get campaigns by time filter
  public getCampaignsByTimeFilter(timeFilter: string): Campaign[] {
    const now = new Date();

    switch (timeFilter) {
      case "today":
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        return this.getCampaignsInDateRange(
          today.toISOString(),
          tomorrow.toISOString()
        );

      case "week":
        const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return this.getCampaignsInDateRange(
          weekStart.toISOString(),
          now.toISOString()
        );

      case "month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return this.getCampaignsInDateRange(
          monthStart.toISOString(),
          now.toISOString()
        );

      case "year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return this.getCampaignsInDateRange(
          yearStart.toISOString(),
          now.toISOString()
        );

      default:
        return this.campaigns;
    }
  }

  // Clear cache (useful for testing or when campaigns might change)
  public clearCache(): void {
    this.campaigns = [];
    this.isLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
    console.log("Campaigns cache cleared");
  }

  // Check if campaigns are loaded
  public isCampaignsLoaded(): boolean {
    return this.isLoaded;
  }

  // Get cached campaigns (synchronous, returns empty array if not loaded)
  public getCachedCampaigns(): Campaign[] {
    return this.campaigns;
  }

  // Refresh campaigns from API
  public async refreshCampaigns(): Promise<Campaign[]> {
    this.clearCache();
    return await this.getCampaigns();
  }
}

// Export singleton instance
export const campaignsService = CampaignsService.getInstance();

// Export types for use in components
export type { Campaign };
